import { Attachment } from "../../common/model/attachment";
import { LocalDate } from "../../core/model/local-date";
import { ModelBase } from "../../core/model/model-base";
import { ErrandStatus } from "../../round-planning/model/const/enums";
import { DwellingInspectionDeviationStatus, DwellingInspectionDeviationStatusToString } from "./const/enums";
import { DwellingInspectionDeviationErrand } from "./dwelling-inspection-deviation-errand";
import { DwellingInspectionPoint } from "./dwelling-inspection-point";

export class DwellingInspectionDeviation extends ModelBase {
  private static readonly EMPTY_COLUMN = '-';

  constructor(dataContext?: DwellingInspectionDeviation) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.dwellingInspectionPointId = dataContext.dwellingInspectionPointId;
      this.dwellingInspectionPoint = dataContext.dwellingInspectionPoint ? new DwellingInspectionPoint(dataContext.dwellingInspectionPoint) : null;
      this.description = dataContext.description;
      this.deviationErrands = dataContext.deviationErrands?.map(x => new DwellingInspectionDeviationErrand(x)) ?? [];
      this.attachments = dataContext.attachments ?? [];
      this.followUp = dataContext.followUp;
      this.followedUp = dataContext.followedUp;
      this.needsErrand = dataContext.needsErrand;
      this.internalNote = dataContext?.internalNote ?? '';
      this.tenantDescription = dataContext?.tenantDescription ?? '';
      this.addressedByTenantAt = dataContext?.addressedByTenantAt ?? null;
      this.inspectionDate = dataContext.inspectionDate ? new LocalDate(dataContext.inspectionDate.year, dataContext.inspectionDate.month, dataContext.inspectionDate.day) : null;
      }
  }

  dwellingInspectionPointId: string;
  dwellingInspectionPoint?: DwellingInspectionPoint;

  description: string;
  followUp: boolean;
  followedUp: boolean;
  needsErrand: boolean;
  internalNote: string;
  tenantDescription: string;
  addressedByTenantAt: string;

  deviationErrands: DwellingInspectionDeviationErrand[] = [];
  attachments: Attachment[] = [];

  get attachmentCount(): string {
    return this.attachments?.length.toString() ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  inspectionDate: LocalDate;

  get tableDescription(): string {
    return this.description?.length > 25 ? this.description.substring(0, 25) + '...' : this.description;
  }

  get tableInternalDescription(): string {
    return this.internalNote?.length > 25 ? this.internalNote.substring(0, 25) + '...' : this.internalNote;
  }

  get errandsProgressText(): string {
    return this.deviationErrands?.length > 0
      ? `${this.deviationErrands.filter(x => this._completedErrandStatuses.includes(x.errand.status)).length}/${this.deviationErrands.length}`
      : DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get errandStatus(): { needsErrand: boolean; errandsProgressText: string } {
    return {
      needsErrand: this.needsErrand,
      errandsProgressText: this.errandsProgressText,
    };
  }

  get deviationStatusText(): string {
    const hasDescription = !!this.tenantDescription?.trim();
    const hasErrands = this.deviationErrands?.length > 0;

    const executedErrandsCount =
      this.deviationErrands?.filter(x =>
        this._completedErrandStatuses.includes(x.errand.status)
      ).length ?? 0;

    const allErrandsExecuted = hasErrands
      ? executedErrandsCount === this.deviationErrands!.length
      : true;

    const addressed = !!this.addressedByTenantAt;

    const tenantResolved = !hasDescription || (hasDescription && addressed);
    const errandsResolved = !hasErrands || allErrandsExecuted;

    // 1) Noted
    if (
      !this.followUp &&
      !this.followedUp &&
      !hasDescription &&
      !hasErrands
    ) {
      return DwellingInspectionDeviationStatusToString(
        DwellingInspectionDeviationStatus.Noted
      );
    }

    // 2) Active
    if (
      this.followUp ||
      (hasDescription && !addressed) ||
      (hasErrands && !allErrandsExecuted)
    ) {
      return DwellingInspectionDeviationStatusToString(
        DwellingInspectionDeviationStatus.Active
      );
    }

    // 3) Closed
    if (
      !this.followUp &&
      tenantResolved &&
      errandsResolved &&
      (
        this.followedUp ||
        hasDescription ||
        hasErrands
      )
    ) {
      return DwellingInspectionDeviationStatusToString(
        DwellingInspectionDeviationStatus.Closed
      );
    }

    return DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get roomText(): string {
    const point = this.dwellingInspectionPoint;

    if (!point) return DwellingInspectionDeviation.EMPTY_COLUMN;
    if (point.dwellingInspectionRoomCaption) return point.dwellingInspectionRoomCaption;

    const room = point.dwellingInspectionRoom;
    if (!room) return DwellingInspectionDeviation.EMPTY_COLUMN;

    return typeof room === 'string' ? room : room.toString();
  }

  get dwellingInspectionTypeName(): string {
    return this.dwellingInspectionPoint?.dwellingInspectionTypeName ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get dwellingInspectionNumber(): string {
    return this.dwellingInspectionPoint?.dwellingInspectionNumber.toString() ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  private _completedErrandStatuses: ErrandStatus[] = [
    ErrandStatus.Executed,
    ErrandStatus.Cancelled,
    ErrandStatus.Closed];

  get componentText(): string {
    return  this.dwellingInspectionPoint?.objectComponent?.name ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get componentCategoryName(): string {
    return  this.dwellingInspectionPoint?.componentCategoryName ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get componentTypeName(): string {
    return  this.dwellingInspectionPoint?.componentTypeName ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get tenantTaskStatus(): string {
    if (this.addressedByTenantAt?.length > 0) return 'DWELLING_INSPECTION_DEVIATION.STATUSES.CLOSED';
    if (this.tenantDescription?.length > 0) return 'DWELLING_INSPECTION_DEVIATION.STATUSES.ACTIVE';
    return "-";
  }

  get assignedResponsibility(): string[] {
    const responsibilities: string[] = [];
    if (this.tenantDescription?.length > 0) responsibilities.push('DWELLING_INSPECTION_DEVIATION.ASSIGNMENTS.TENANT');
    if (this.deviationErrands?.length > 0) responsibilities.push('DWELLING_INSPECTION_DEVIATION.ASSIGNMENTS.LANDLORD');

    return responsibilities;
  }

  get photoCount(): string {
    return this.attachments?.length.toString() ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get costText(): string {
    const payment = this.dwellingInspectionPoint?.payment;
    if (!payment) return DwellingInspectionDeviation.EMPTY_COLUMN;
    if (payment.amount !== 0) return payment.toString();
    return DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get deviationNumber(): string {
    return DwellingInspectionDeviation.EMPTY_COLUMN;
  }

  get inspectionDateText(): string {
    return this.inspectionDate?.toString() ?? DwellingInspectionDeviation.EMPTY_COLUMN;
  }
}

export class DwellingInspectionDeviationForm extends ModelBase {
  constructor(dataContext?: DwellingInspectionDeviation, deletedAttachmentIds?: string[]) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.dwellingInspectionPointId = dataContext.dwellingInspectionPointId;
      this.description = dataContext.description;
      this.followUp = dataContext.followUp;
      this.followedUp = dataContext.followedUp || dataContext.followUp;
      this.needsErrand = dataContext.needsErrand;
      this.internalNote = dataContext?.internalNote ?? '';
      this.tenantDescription = dataContext?.tenantDescription ?? '';
      this.addressedByTenantAt = dataContext?.addressedByTenantAt ?? null;
      
      this.addedAttachments = dataContext.attachments.filter(x => !x.id);
    }

    this.deletedAttachmentIds = deletedAttachmentIds ?? [];
  }

  dwellingInspectionPointId: string;
  description: string;
  followUp: boolean;
  followedUp: boolean;
  needsErrand: boolean;
  internalNote: string;
  tenantDescription: string;
  addressedByTenantAt: string;

  addedAttachments: any[];
  deletedAttachmentIds: string[];

  toJSON() {
    return {
      ...this,
      addedAttachments: this.addedAttachments.map(() => ({})),
    };
  }
}
